// This file is part of ecbyts/ECBT (Environmental & Occupational Core Byte Tools).
// Copyright (C) 2026 Calvin Stefan Iost
// Licensed under the GNU Affero General Public License v3.0 (AGPL-3.0-only).
// See LICENSE file or https://www.gnu.org/licenses/agpl-3.0.txt

import { SUPABASE_URL } from '../../supabase-config.js';

const MANIFEST_URL = './assets/docs/videos/docs-videos.manifest.json';

/**
 * Backend do deploy, sem barra final. '' quando o build nao tem config (o gerador
 * grava `https://placeholder.supabase.co`) — mesma degradacao de `config.ts`.
 */
const RUNTIME_SUPABASE_URL: string =
    SUPABASE_URL && !/placeholder/.test(SUPABASE_URL) ? SUPABASE_URL.replace(/\/+$/, '') : '';

export interface VideoManifestItem {
    sectionId: string;
    title: string;
    description: string;
    poster?: string | null;
    publicUrl?: string | null;
    url?: string | null;
    sourceType?: string | null;
    qualityStatus?: string | null;
    mediaMode?: 'curated_video' | 'contextual_preview' | null;
    transcript?: { language: string; steps: Array<{ title: string; text: string }> } | null;
}

export interface VideoManifest {
    /** Host com que este manifesto foi gerado (`upload-docs-videos-to-supabase-storage.mjs`). */
    supabaseUrl?: string | null;
    items?: VideoManifestItem[];
}

/**
 * SP4 (separacao prod/homolog): o manifesto e um JSON versionado, gerado contra o
 * backend de PRODUCAO, com 227 `publicUrl` absolutos gravados no arquivo
 * (`docs-videos.manifest.json:20` e seguintes). Servir esses literais faria um
 * build de homologacao baixar video e poster do storage de producao.
 *
 * Em vez de reescrever 227 linhas do JSON gerado (que o proximo `--no-manifest`
 * desfaz), a troca de host acontece no UNICO ponto que o manifesto tem para
 * escolher o backend: a leitura. O manifesto declara em `supabaseUrl` contra qual
 * host foi gerado; quando ele difere do configurado no build, o caminho e o
 * bucket do objeto sao preservados e so o host muda.
 *
 * Conservador de proposito: so reescreve URL cujo host e EXATAMENTE o declarado
 * (nao mexe em asset relativo, em `./assets/...` nem em terceiro) e so quando ha
 * config de verdade (sem `RUNTIME_SUPABASE_URL`, o manifesto fica como esta — sem
 * config nao existe destino melhor que o declarado).
 */
export function retargetToConfiguredBackend(url: unknown, declaredBase: string): string {
    const value = String(url ?? '');
    if (!RUNTIME_SUPABASE_URL || !declaredBase || !/^https?:\/\//i.test(value)) return value;
    try {
        const alvo = new URL(value);
        const declarado = new URL(declaredBase);
        if (alvo.origin !== declarado.origin) return value;
        return `${RUNTIME_SUPABASE_URL}${alvo.pathname}${alvo.search}`;
    } catch {
        return value;
    }
}

/** Aplica a troca de host do manifesto inteiro uma unica vez, na carga. */
export function retargetManifest(manifest: VideoManifest): void {
    const declaredBase = String(manifest.supabaseUrl || '').replace(/\/+$/, '');
    if (!RUNTIME_SUPABASE_URL || !declaredBase || declaredBase === RUNTIME_SUPABASE_URL) return;
    for (const item of manifest.items || []) {
        if (item?.publicUrl) item.publicUrl = retargetToConfiguredBackend(item.publicUrl, declaredBase);
        if (item?.poster) item.poster = retargetToConfiguredBackend(item.poster, declaredBase);
    }
}

function escapeHtml(value: unknown): string {
    return String(value ?? '').replace(/[&<>"']/g, (ch) => {
        switch (ch) {
            case '&':
                return '&amp;';
            case '<':
                return '&lt;';
            case '>':
                return '&gt;';
            case '"':
                return '&quot;';
            case "'":
                return '&#39;';
            default:
                return ch;
        }
    });
}

/* ================================================================
   I18N DOS CARDS — REUSO DAS CHAVES QUE JA EXISTEM

   Os cards eram montados com `title`/`description` fixos do manifesto, sem
   nenhum i18n: medido em 2026-08-18, 100 dos 115 cards ficavam em ingles com a
   pagina em portugues, logo abaixo de cada titulo ja traduzido — a mistura de
   idiomas mais visivel da documentacao.

   Nao ha chave nova para titulo/descricao: o proprio `<h2 id="sectionId">` que
   o card ancora ja carrega `data-i18n`, e o primeiro paragrafo da secao ja e
   `doc.content.<sectionId>.p1`. O card le essas duas, e cai no texto do
   manifesto quando a chave nao existir.
   ================================================================ */

/** Traducao da docs (docsInit expoe `__ecbytsDocsT`), com fallback declarado. */
function docsT(key: string, fallback: string, vars?: Readonly<Record<string, string>>): string {
    const fn = (window as any).__ecbytsDocsT as ((k: string, f?: string) => string) | undefined;
    let value = key && typeof fn === 'function' ? fn(key, '') : '';
    if (!value) value = fallback;
    if (vars) for (const [name, v] of Object.entries(vars)) value = value.split(`{${name}}`).join(v);
    return value;
}

/** true quando a chave EXISTE no locale ativo — evita marcar chave orfa. */
function temChave(key: string): boolean {
    const fn = (window as any).__ecbytsDocsT as ((k: string, f?: string) => string) | undefined;
    return Boolean(key && typeof fn === 'function' && fn(key, ''));
}

function firstScreenshotAfter(anchor: unknown): HTMLElement | null {
    const anchorElement = anchor as HTMLElement | null;
    let node = anchorElement?.nextElementSibling as HTMLElement | null;
    while (node && !/^H2$/i.test(node.tagName)) {
        if (node.classList?.contains('doc-screenshot')) return node;
        node = node.nextElementSibling as HTMLElement | null;
    }
    return null;
}

const DIRECT_TOOL_BY_SECTION: Readonly<Record<string, string>> = Object.freeze({
    accessibility: 'accessibility',
    analytics: 'analytics',
    automation: 'automation',
    'campaign-results': 'campaign-results',
    'cost-analysis': 'cost-analysis',
    'data-entry-matrix': 'data-entry',
    diagrams: 'diagram-editor',
    eis: 'eis',
    'filter-radar': 'radar',
    'fine-tuning': 'training',
    'health-export': 'health-monitor',
    'libraries-marketplace': 'libraries',
    notifications: 'notifications',
    overview: 'showcase',
    photolog: 'photolog',
    pipelines: 'pipelines',
    radar: 'radar',
    reports: 'report-builder',
    'regulatory-applicability': 'regulatory-applicability',
    'scenario-simulation': 'simulation',
    simulation: 'simulation',
    'scene-3d': 'constellation-controls',
    interface: 'constellation-controls',
    'sustainability-reporting': 'sustainability-reporting',
    'symbology-profiles': 'symbology',
    'system-catalog': 'system-catalog',
    uncertainty: 'uncertainty-hub',
    wallet: 'wallet',
});

function encodePayload(value: unknown): string {
    const bytes = new TextEncoder().encode(JSON.stringify(value));
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function catalogQuery(item: VideoManifestItem): string {
    const row = Array.from(document.querySelectorAll('tr[data-home-module-id]')).find((entry) =>
        entry.querySelector(`a[href="#${CSS.escape(item.sectionId)}"]`),
    );
    const moduleLabel = row?.querySelector('strong')?.textContent?.trim();
    return moduleLabel || item.title;
}

function moduleDeepLink(item: VideoManifestItem): { href: string; label: string; direct: boolean } {
    const directTool = DIRECT_TOOL_BY_SECTION[item.sectionId];
    if (directTool) {
        return {
            href: `./?tools=${encodeURIComponent(directTool)}`,
            label: docsT('doc.video.openModule', 'Open module'),
            direct: true,
        };
    }
    const payload = encodePayload({ query: catalogQuery(item), sectionId: item.sectionId });
    return {
        href: `./?tools=system-catalog&payload=${payload}`,
        label: docsT('doc.video.findInCatalog', 'Find module in catalog'),
        direct: false,
    };
}

function isCuratedVideo(item: VideoManifestItem): boolean {
    return (
        item.mediaMode === 'curated_video' ||
        item.qualityStatus === 'curated_module_video'
    ) && Boolean(item.publicUrl || item.url);
}

function usefulPoster(item: VideoManifestItem, screenshot: HTMLElement | null): string {
    const contextual = screenshot?.getAttribute('src') || '';
    if (contextual) return contextual;
    const poster = String(item.poster || '');
    return /\/assets\/logo\.png(?:$|[?#])/i.test(poster) ? '' : poster;
}

/** Optional accessible supplement; manifest strings are text, never markup. */
export function appendVideoAccessibility(card: HTMLElement, item: VideoManifestItem): void {
    if (!isCuratedVideo(item)) return;
    const language = document.documentElement.lang.split('-')[0] || 'en';
    const labels: Record<string, { transcript: string; download: string }> = {
        pt: { transcript: 'Transcrição do vídeo', download: 'Baixar vídeo (MP4)' },
        en: { transcript: 'Video transcript', download: 'Download video (MP4)' },
        es: { transcript: 'Transcripción del vídeo', download: 'Descargar vídeo (MP4)' },
    };
    const label = labels[language] ?? labels.en!;
    let downloadUrl: URL | null = null;
    try {
        const url = new URL(item.publicUrl || '', document.baseURI);
        if (item.publicUrl && ['http:', 'https:'].includes(url.protocol) &&
            url.origin === new URL(document.baseURI).origin && !url.username && !url.password)
            downloadUrl = url;
    } catch (error) {
        // Do not log the rejected URL; it may contain credentials or query data.
        console.debug('[docs-video] invalid download URL', error instanceof Error ? error.name : 'InvalidURL');
    }
    if (downloadUrl) {
        const link = document.createElement('a');
        link.href = downloadUrl.href;
        link.setAttribute('download', '');
        link.className = 'docs-video-download';
        link.textContent = docsT('doc.video.download', label.download);
        (card.querySelector('.docs-media-actions') || card).appendChild(link);
    }
    const transcript = item.transcript;
    if (!transcript || typeof transcript.language !== 'string' ||
        !/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(transcript.language) ||
        !Array.isArray(transcript.steps) || !transcript.steps.length ||
        !transcript.steps.every(step => step && typeof step.title === 'string' && step.title.trim() &&
            typeof step.text === 'string' && step.text.trim())) return;
    const details = document.createElement('details');
    details.className = 'docs-video-transcript';
    const summary = document.createElement('summary');
    summary.textContent = `${docsT('doc.video.transcript', label.transcript)} (${transcript.language})`;
    details.appendChild(summary);
    const list = document.createElement('ol');
    list.lang = transcript.language;
    for (const step of transcript.steps) {
        const li = document.createElement('li');
        const title = document.createElement('strong');
        title.textContent = step.title;
        const text = document.createElement('p');
        text.textContent = step.text;
        li.appendChild(title);
        li.appendChild(text);
        list.appendChild(li);
    }
    details.appendChild(list);
    card.appendChild(details);
}

function buildVideoCard(item: unknown, screenshot: HTMLElement | null, anchor: HTMLElement | null): HTMLElement {
    const videoItem = item as VideoManifestItem;
    const curated = isCuratedVideo(videoItem);
    const poster = usefulPoster(videoItem, screenshot);
    const moduleLink = moduleDeepLink(videoItem);
    // titulo: a chave vive no proprio <h2> que o card ancora — zero mapa novo.
    const tituloKey = anchor?.getAttribute('data-i18n') || '';
    const titulo = docsT(tituloKey, videoItem.title);
    // descricao: o primeiro paragrafo da secao, que ja e HTML confiavel do repo.
    const descKey = `doc.content.${videoItem.sectionId}.p1`;
    const descTraduzida = temChave(descKey);
    const descricao = descTraduzida
        ? docsT(descKey, '')
        : escapeHtml(videoItem.description);
    const eyebrow = curated
        ? docsT('doc.video.guidedWalkthrough', 'Guided walkthrough')
        : docsT('doc.video.modulePreview', 'Module preview');
    const card = document.createElement('section');
    card.className = `docs-video-card docs-video-card--${curated ? 'curated' : 'preview'}${poster ? '' : ' docs-video-card--text-only'}`;
    card.dataset.docsVideoSection = videoItem.sectionId;
    card.dataset.docsMediaMode = curated ? 'curated_video' : 'contextual_preview';
    card.dataset.docsModuleLink = moduleLink.direct ? 'direct' : 'catalog';
    const media = curated
        ? `<div class="docs-video-frame">
            <video controls preload="none" playsinline poster="${escapeHtml(poster)}"
                title="${escapeHtml(docsT('doc.video.ariaWalkthrough', 'Guided walkthrough: {title}', { title: titulo }))}"
                aria-label="${escapeHtml(docsT('doc.video.ariaWalkthroughFor', 'Guided walkthrough for {title}', { title: titulo }))}"
                src="${escapeHtml(videoItem.publicUrl || videoItem.url || '')}"></video>
           </div>`
        : poster
            ? `<a class="docs-media-preview" href="${escapeHtml(moduleLink.href)}" target="_blank" rel="noopener" aria-label="${escapeHtml(docsT('doc.video.ariaOpenIn', 'Open {title} in ecbyts', { title: titulo }))}">
                <img src="${escapeHtml(poster)}" alt="${escapeHtml(docsT('doc.video.altPreview', 'Focused product preview for {title}', { title: titulo }))}" loading="lazy" decoding="async">
                <span>${escapeHtml(docsT('doc.video.openInEcbyts', 'Open in ecbyts'))}</span>
               </a>`
            : '';
    card.innerHTML = `
        <div class="docs-video-copy">
            <p class="docs-video-eyebrow">${escapeHtml(eyebrow)}</p>
            <h3>${escapeHtml(titulo)}</h3>
            <p>${descricao}</p>
        </div>
        ${media}
        <div class="docs-media-actions">
            <a class="docs-module-open" href="${escapeHtml(moduleLink.href)}" target="_blank" rel="noopener">${escapeHtml(moduleLink.label)}</a>
            ${curated ? `<a href="${escapeHtml(videoItem.publicUrl || videoItem.url || '#')}" target="_blank" rel="noopener">${escapeHtml(docsT('doc.video.openVideo', 'Open video'))}</a>` : ''}
        </div>
    `;
    appendVideoAccessibility(card, videoItem);
    return card;
}

function insertVideo(item: unknown): void {
    const videoItem = item as VideoManifestItem;
    if (!videoItem?.sectionId) return;
    const sectionSelector = String(videoItem.sectionId).replace(/["\\]/g, '\\{{ITEM_CONTENT}}');
    if (document.querySelector(`[data-docs-video-section="${sectionSelector}"]`)) return;

    const anchor = document.getElementById(videoItem.sectionId);
    if (!anchor) return;

    const screenshot = firstScreenshotAfter(anchor);
    const card = buildVideoCard(videoItem, screenshot, anchor);
    if (screenshot) {
        screenshot.insertAdjacentElement('afterend', card);
    } else {
        anchor.insertAdjacentElement('afterend', card);
    }
}

/** Ultimo manifesto carregado — guardado para remontar ao trocar de idioma. */
let manifestoAtual: VideoManifestItem[] = [];

function renderCards(): void {
    for (const card of Array.from(document.querySelectorAll('[data-docs-video-section]'))) {
        card.remove();
    }
    for (const item of manifestoAtual) insertVideo(item);
}

/**
 * O card e montado por string, nao por `data-i18n`, entao `applyTranslations()`
 * do docsInit nao o alcanca. O sinal de troca de idioma que o docsInit deixa
 * publico e `document.documentElement.lang` — observa-lo e remontar mantem os
 * cards no mesmo idioma da pagina, inclusive nos `aria-label` interpolados,
 * que um `data-i18n` nao saberia preencher.
 */
function observarTrocaDeIdioma(): void {
    let idiomaAnterior = document.documentElement.lang;
    new MutationObserver(() => {
        const agora = document.documentElement.lang;
        if (agora === idiomaAnterior) return;
        idiomaAnterior = agora;
        renderCards();
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
}

async function initDocsVideoEmbeds(): Promise<void> {
    try {
        const response = await fetch(MANIFEST_URL, { cache: 'no-cache' });
        if (!response.ok) return;
        const manifest = (await response.json()) as VideoManifest;
        retargetManifest(manifest);
        manifestoAtual = manifest.items || [];
        renderCards();
        observarTrocaDeIdioma();
    } catch (err) {
        console.warn('[docs] video manifest unavailable:', (err as Error)?.message || err);
    }
}

initDocsVideoEmbeds();
